import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';

import Input from '@material-ui/core/Input';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import { TagInfo } from '~/interfaces';
import log from '~/helpers/log';
import api from '~/services';
import * as actionTypes from '~/store/actions/actionTypes';
import { filterOptions } from '~/store/reducers/historyFilterOptions';

import {
  FormControl,
  MenuProps,
  MenuItem,
  ListItemText,
} from '../sharedStyles';

export default function TagsSelector() {
  const filterName = 'Tags';

  const dispatch = useDispatch();

  const [open, setOpen] = useState<boolean>(false);
  const [tagsList, setTagsList] = useState<TagInfo[]>();

  const selectedTags = useSelector(
    (state: RootStateOrAny) => state.historyFilterOptions.tags
  );

  const getAllTags = useCallback(async () => {
    try {
      const tags: TagInfo[] = (await api.tags.get.allTags())?.data;
      setTagsList(tags);
    } catch (err) {
      log.error('Failed to fetch all tags.', err);
    }

    return [];
  }, [setTagsList]);

  useEffect(() => {
    if (selectedTags?.length === 0) {
      getAllTags();
    }
  }, [selectedTags, getAllTags]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSelectedTags = event.target.value as string[];

    dispatch({
      type: actionTypes.SET_HISTORY_FILTER_OPTIONS,
      option: filterOptions.tags,
      payload: newSelectedTags,
      extra: { resetCallItems: true },
    });
  };

  const handleToggle = (state: boolean) => {
    setOpen(state);
  };

  const handlePlaceholder = () => {
    const hasSelectedTags = selectedTags.length > 0;
    if (open || (hasSelectedTags && !open)) {
      return selectedTags;
    }
    return ['Tags'];
  };

  const renderValue = (values: string[]) => {
    const [valueOne] = values;

    if (Array.isArray(values) && valueOne !== filterName && tagsList) {
      const associatedNames = values.map(tagId => {
        const tagInfo = tagsList.find(item => item.tag_id === tagId);
        return tagInfo && tagInfo.tag_name ? tagInfo.tag_name : tagId;
      });

      return `Tags: ${associatedNames.filter(name => name).join(', ')}`;
    }

    return values;
  };

  return (
    <div>
      <FormControl variant="outlined" hasselecteditems={selectedTags?.length}>
        <Select
          id="select-tags"
          labelId="select-tags"
          multiple
          disableUnderline
          input={<Input />}
          onOpen={() => handleToggle(true)}
          onClose={() => handleToggle(false)}
          onChange={handleChange}
          value={handlePlaceholder()}
          // @ts-ignore
          renderValue={renderValue}
          // @ts-ignore
          MenuProps={MenuProps}>
          <MenuItem firstItem value="Tags" disabled>
            <ListItemText firstItem primary="Tags" />
          </MenuItem>
          {tagsList?.length &&
            tagsList?.map(tag => (
              <MenuItem
                key={tag.tag_id}
                value={tag.tag_id}
                background={tag.tag_color}>
                <ListItemText fontWeight="bold" color="white">
                  {tag.tag_name}
                </ListItemText>
                <Checkbox checked={selectedTags.indexOf(tag.tag_id) > -1} />
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </div>
  );
}
